import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import clsx from 'clsx';
import { AuthContext } from '../contexts/AuthContext';
import { categories } from '../utils/staticData';
import { createGroup, apiErrorMessage } from '../services/api';
import FloatingInput from '../components/ui/FloatingInput';
import SegmentedControl from '../components/ui/SegmentedControl';

// ---- SVG icons ----
const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const LockIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const GlobeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ImageIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const TagIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

// ---- Form section wrapper ----
function FormSection({ icon, title, children }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-primary-500">{icon}</span>
        <h3 className="text-body font-semibold text-surface-700">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// ---- Main component ----
function CreateGroupCliqq() {
  const { currentUser } = React.useContext(AuthContext);
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    privacy: 'public',
    requireApproval: false,
    categoryId: '',
    imageUrl: '',
    bannerUrl: '',
    tags: '',
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  const validate = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Group name is required.';
    if (!formData.description.trim()) errors.description = 'Description is required.';
    return errors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePrivacyChange = (value) => {
    setFormData((prev) => ({ ...prev, privacy: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error('Please fix the highlighted fields before submitting.');
      return;
    }

    setSubmitting(true);

    try {
      if (!currentUser) {
        toast.error('You must be logged in to create a group.');
        setSubmitting(false);
        return;
      }

      await createGroup({
        name: formData.name,
        description: formData.description,
        privacy: formData.privacy,
        require_approval: formData.requireApproval,
        category_id: formData.categoryId ? parseInt(formData.categoryId) : null,
        image_url: formData.imageUrl,
        banner_url: formData.bannerUrl,
        tags: formData.tags.split(',').map((t) => t.trim()).filter(Boolean),
      });

      toast.success('Group CliQQ created successfully!');
      setTimeout(() => {
        // There is no /groups/:id route (no group detail page exists yet), so
        // navigating there 404'd immediately after every successful creation.
        // Send the user to the Social page, which lists their Group CliQQs.
        navigate('/social');
      }, 800);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to create group. Please try again.'));
      setSubmitting(false);
    }
  };

  const privacyOptions = [
    {
      value: 'public',
      label: 'Public',
      icon: <GlobeIcon />,
    },
    {
      value: 'private',
      label: 'Private',
      icon: <LockIcon />,
    },
  ];

  return (
    <div className="min-h-screen bg-surface-50 font-sans">
      {/* Top bar */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 px-4 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-xl bg-white/15 text-white hover:bg-white/25 flex items-center justify-center transition-colors duration-200"
          aria-label="Go back"
        >
          <ChevronLeftIcon />
        </button>
        <span className="text-white text-body font-bold tracking-wide">CliQQ</span>
        <div className="w-9" aria-hidden="true" />
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="max-w-2xl mx-auto mt-8 mb-12 mx-4 sm:mx-auto"
      >
        <div className="bg-white rounded-2xl shadow-soft-md border border-surface-100 overflow-hidden">
          {/* Card header */}
          <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-7 py-6 border-b border-surface-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-primary-500 text-white flex items-center justify-center shadow-colored">
                <UsersIcon />
              </div>
              <div>
                <h1 className="text-h2 text-surface-900">Create a Group CliQQ</h1>
                <p className="text-body-sm text-surface-500 mt-0.5">Build a community around your shared interests</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-7 py-7 space-y-8" noValidate>
            {/* Basic info */}
            <FormSection icon={<UsersIcon />} title="Basic Information">
              <FloatingInput
                label="Group Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                error={fieldErrors.name}
              />
              <div className="relative">
                <textarea
                  name="description"
                  id="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder=" "
                  className={clsx(
                    'peer w-full bg-surface-50 border rounded-xl text-surface-900 pt-6 pb-3 px-4 resize-none',
                    'focus:ring-2 focus:bg-white focus:outline-none transition-all duration-200 text-body',
                    fieldErrors.description
                      ? 'border-error-500 focus:ring-error-500/20 focus:border-error-500'
                      : 'border-surface-200 focus:ring-primary-500/20 focus:border-primary-500'
                  )}
                />
                <label
                  htmlFor="description"
                  className={clsx(
                    'absolute left-4 transition-all duration-200 pointer-events-none select-none',
                    'peer-placeholder-shown:top-4 peer-placeholder-shown:text-body peer-placeholder-shown:text-surface-400',
                    'peer-focus:top-2 peer-focus:text-caption peer-focus:text-primary-600',
                    'top-2 text-caption',
                    fieldErrors.description ? 'text-error-500' : 'text-surface-500'
                  )}
                >
                  Group Description<span className="text-error-500 ml-0.5">*</span>
                </label>
                {fieldErrors.description && (
                  <p className="text-caption text-error-600 mt-1.5 px-1">{fieldErrors.description}</p>
                )}
              </div>
            </FormSection>

            {/* Privacy */}
            <FormSection icon={<LockIcon />} title="Privacy Settings">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <SegmentedControl
                  name="privacy"
                  label="Visibility"
                  options={privacyOptions}
                  value={formData.privacy}
                  onChange={handlePrivacyChange}
                />
                <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
                  <div className="relative">
                    <input
                      type="checkbox"
                      name="requireApproval"
                      checked={formData.requireApproval}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <div className={clsx(
                      'w-10 h-5 rounded-full transition-colors duration-200',
                      formData.requireApproval ? 'bg-primary-500' : 'bg-surface-200'
                    )} />
                    <div className={clsx(
                      'absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200',
                      formData.requireApproval && 'translate-x-5'
                    )} />
                  </div>
                  <span className="text-body-sm text-surface-700 font-medium">Require approval to join</span>
                </label>
              </div>
              <p className="text-caption text-surface-400">
                {formData.privacy === 'private'
                  ? 'Only invited members can find and join this group.'
                  : 'Anyone can find this group. Approval controls who can join.'}
              </p>
            </FormSection>

            {/* Category */}
            <FormSection icon={<TagIcon />} title="Category">
              <div className="relative">
                <select
                  name="categoryId"
                  id="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  className="w-full bg-surface-50 border border-surface-200 rounded-xl text-body text-surface-900 px-4 py-3 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-surface-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </FormSection>

            {/* Tags */}
            <FormSection icon={<TagIcon />} title="Custom Tags">
              <FloatingInput
                label="Tags (comma-separated)"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder=" "
              />
              <p className="text-caption text-surface-400 -mt-1 px-1">
                e.g. social, networking, professional
              </p>
            </FormSection>

            {/* Images */}
            <FormSection icon={<ImageIcon />} title="Group Images">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FloatingInput
                  label="Avatar Image URL"
                  name="imageUrl"
                  type="url"
                  value={formData.imageUrl}
                  onChange={handleChange}
                />
                <FloatingInput
                  label="Banner Image URL"
                  name="bannerUrl"
                  type="url"
                  value={formData.bannerUrl}
                  onChange={handleChange}
                />
              </div>
            </FormSection>

            {/* Actions */}
            <div className="flex items-center justify-between pt-2 border-t border-surface-100">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-2.5 bg-surface-100 text-surface-700 rounded-xl text-body-sm font-semibold hover:bg-surface-200 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className={clsx(
                  'flex items-center gap-2 px-7 py-2.5 rounded-xl text-body-sm font-semibold transition-all duration-200',
                  submitting
                    ? 'bg-primary-300 text-white cursor-not-allowed'
                    : 'bg-primary-500 text-white hover:bg-primary-600 shadow-colored hover:shadow-colored'
                )}
              >
                {submitting && (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {submitting ? 'Creating...' : 'Create Group'}
              </button>
            </div>
          </form>

          {/* Progress bar */}
          <div className="px-7 py-4 bg-surface-50 border-t border-surface-100">
            <div className="flex items-center justify-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-caption font-semibold text-primary-600">Join</span>
              </div>
              <div className="h-px w-12 bg-primary-300 rounded-full" />
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-white">2</span>
                </div>
                <span className="text-caption font-semibold text-primary-600">My page</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default CreateGroupCliqq;
